/* =====================================================================
   KCIT promo banner integration
   Spec: C:\Users\Chris\iis-kcit-co\SHOPIFY_BANNER_INTEGRATION.md (v4.1)
   Theme: assets/kcit-banner.css + theme.liquid #kcit-banner-slot + window.__sfx
   Doc:   .claude/docs/BANNER_SYSTEM.md

   What this does:
     1. Fetch banner envelope from kcit.co/stagefx-app/api/banner
     2. Filter banners by variant (window.__sfx.variant) + collections
        (window.__sfx.collections) + dismissal cookie
     3. Render the first matching one (with optional rotation through
        multiple matches every rotation_seconds)
     4. Honour countdown timers (server_time anchored), dismissal, and
        CTA actions (link / modal / article)
     5. Fail silently — never break the page

   Variant slug shim (per Chris's "Both shims (defensive)" decision):
     Our theme uses `lightingtruss`. The KCIT spec used `truss` as a
     placeholder. We send `lightingtruss` as primary; if a banner is
     targeted to `truss` we ALSO match (only when our variant is
     lightingtruss). Remove the shim once KCIT _config.php confirms
     they're using `lightingtruss`.
   ===================================================================== */
(function () {
  'use strict';

  var API_URL = 'https://kcit.co/stagefx-app/api/banner';
  var DISMISS_DAYS = 30;

  var sfx = window.__sfx || {};
  var variant = sfx.variant || null;
  var pageCollections = sfx.collections || [];

  if (!variant) {
    console.warn('[kcit-banner] No variant — skipping. Ensure window.__sfx.variant is set in theme.liquid.');
    return;
  }

  fetch(API_URL, { credentials: 'omit' })
    .then(function (r) { return r.ok ? r.json() : null; })
    .then(function (env) {
      if (!env || !env.banners) return;
      var matching = env.banners.filter(function (b) {
        return matchesVariant(b)
            && matchesCollections(b)
            && isLive(b)
            && !isDismissed(b);
      });
      if (matching.length === 0) return;
      mount(env, matching);
    })
    .catch(function (e) { /* silent — no banner on network error */ });

  /* ----- filters ----- */
  function matchesVariant(b) {
    var audiences = b.audience || [];
    if (audiences.indexOf(variant) !== -1) return true;
    // Defensive shim: KCIT used `truss` as the Lighting Truss slug.
    if (variant === 'lightingtruss' && audiences.indexOf('truss') !== -1) return true;
    return false;
  }
  function matchesCollections(b) {
    if (!b.collections || b.collections.length === 0) return true;
    return pageCollections.some(function (c) { return b.collections.indexOf(c) !== -1; });
  }
  function isLive(b) {
    if (b.active) return true;
    // Pre-launch countdown — show the banner even though active=false IF
    // the countdown is targeting the start date
    if (b.countdown && b.countdown.enabled &&
        (b.countdown.target === 'start' || b.countdown.target === 'auto')) return true;
    return false;
  }
  function isDismissed(b) {
    if (!b.dismissable) return false;
    var name = 'kcit_b_' + b.id + '=';
    var match = document.cookie.split('; ').find(function (c) { return c.indexOf(name) === 0; });
    if (!match) return false;
    return decodeURIComponent(match.slice(name.length)) === b.updated_at;
  }
  function dismissCookieSet(b) {
    document.cookie = 'kcit_b_' + b.id + '=' + encodeURIComponent(b.updated_at) +
                      '; max-age=' + (DISMISS_DAYS * 86400) +
                      '; path=/; SameSite=Lax';
  }

  /* ----- mount + rotation ----- */
  function mount(env, banners) {
    var slot = document.getElementById('kcit-banner-slot') || createSlot();
    var idx = 0;
    var clockOffset = new Date(env.server_time).getTime() - Date.now();
    var rotationMs = (env.rotation_seconds || 6) * 1000;
    var currentTick = null;
    var rotationTimer = null;
    var paused = false;

    // --- "Absolute top" pinned mode: keep --kcit-top equal to the live banner
    //     height so the ticker/header/hero offsets (in kcit-banner.css) track it.
    function measurePinnedTop() {
      if (!document.documentElement.classList.contains('kcit-has-top-banner')) return;
      document.documentElement.style.setProperty('--kcit-top', slot.offsetHeight + 'px');
    }
    function clearPinnedTop() {
      slot.classList.remove('kcit-banner--pinned-top');
      document.documentElement.classList.remove('kcit-has-top-banner');
      document.documentElement.style.removeProperty('--kcit-top');
    }
    function applyPinnedTop(location) {
      if (location === 'top-in-flow' || location === 'top-sticky') {
        slot.classList.add('kcit-banner--pinned-top');
        document.documentElement.classList.add('kcit-has-top-banner');
        measurePinnedTop();
      } else {
        clearPinnedTop();
      }
    }
    // Re-measure on reflow (resize, countdown text change, rotation to a
    // taller/shorter banner) so the page offset always matches.
    if (window.ResizeObserver) {
      new ResizeObserver(measurePinnedTop).observe(slot);
    } else {
      window.addEventListener('resize', measurePinnedTop);
    }

    function render(b) {
      // Strip prior modifiers, add fresh ones
      var location = b.location || 'top-in-flow';
      var bgColor = b.bg_color || 'stagefx-black';
      // bg_color can be EITHER a palette key (e.g. "holi-magenta") OR a raw
      // hex string (e.g. "#ce15b4"). Hex needs inline style — class name
      // can't contain "#".
      if (bgColor.charAt(0) === '#') {
        slot.className = 'kcit-banner kcit-banner--' + location;
        slot.style.background = bgColor;
      } else {
        slot.className = 'kcit-banner kcit-banner--' + location +
                         ' kcit-banner--' + bgColor;
        slot.style.background = '';
      }
      if (b.fg_color) slot.style.color = b.fg_color;
      else slot.style.color = '';
      slot.innerHTML = renderHtml(b, clockOffset);
      wireBanner(b);
      applyPinnedTop(location);
      // Start countdown tick if applicable
      if (currentTick) { clearInterval(currentTick); currentTick = null; }
      if (b.countdown && b.countdown.enabled) {
        currentTick = setInterval(function () {
          var newHtml = renderHtml(b, clockOffset);
          if (!newHtml) {
            // on_expiry: hide
            slot.classList.add('is-leaving');
            clearPinnedTop();
            clearInterval(currentTick); currentTick = null;
          } else {
            slot.innerHTML = newHtml;
            wireBanner(b);
          }
        }, 1000);
      }
    }

    function renderHtml(b, clockOffset) {
      var ctx = computeCountdownContext(b, clockOffset);
      if (ctx.suppressed) return null; // on_expiry === 'hide' after expiry
      var msg = ctx.replacementMessage || b.message || '';
      var cdHtml = ctx.html;
      if (cdHtml) {
        if (msg.indexOf('{countdown}') !== -1) {
          msg = escape(msg).replace('{countdown}', cdHtml);
        } else {
          msg = escape(msg) + ' &mdash; ' + cdHtml;
        }
      } else {
        msg = escape(msg);
      }
      // CTA — uses url_label as button text regardless of action.
      // For link: render <a href>. For modal/article: render <button>
      // (JS attaches a click handler).
      var ctaHtml = '';
      if (b.url_label) {
        var action = b.cta_action || 'link';
        if (action === 'modal' && b.modal_page_handle) {
          ctaHtml = '<button class="kcit-banner__cta" type="button" data-cta="modal" data-handle="' + escape(b.modal_page_handle) + '">' + escape(b.url_label) + '</button>';
        } else if (action === 'article' && b.article_path) {
          ctaHtml = '<button class="kcit-banner__cta" type="button" data-cta="article" data-path="' + escape(b.article_path) + '">' + escape(b.url_label) + '</button>';
        } else if (action === 'link' && b.url) {
          ctaHtml = '<a class="kcit-banner__cta" href="' + escape(b.url) + '">' + escape(b.url_label) + '</a>';
        }
      }
      var dismissBtn = b.dismissable ?
        '<button class="kcit-banner__close" type="button" aria-label="Dismiss">&times;</button>' : '';
      return '<span class="kcit-banner__msg">' + msg + '</span>' + ctaHtml + dismissBtn;
    }

    function wireBanner(b) {
      // CTA modal/article handlers
      var ctaBtn = slot.querySelector('.kcit-banner__cta[data-cta]');
      if (ctaBtn) {
        ctaBtn.addEventListener('click', function (e) {
          e.preventDefault();
          var action = this.getAttribute('data-cta');
          if (action === 'modal') {
            openContentModal('/pages/' + this.getAttribute('data-handle') + '?view=raw', b.url_label, '/pages/' + this.getAttribute('data-handle'));
          } else if (action === 'article') {
            openContentModal('/blogs/' + this.getAttribute('data-path') + '?view=raw', b.url_label, '/blogs/' + this.getAttribute('data-path'));
          }
        });
      }
      // Dismiss handler
      var dismissBtn = slot.querySelector('.kcit-banner__close');
      if (dismissBtn) {
        dismissBtn.addEventListener('click', function () {
          dismissCookieSet(b);
          slot.classList.add('is-leaving');
          clearPinnedTop();
          setTimeout(function () { slot.remove(); }, 350);
          if (rotationTimer) { clearInterval(rotationTimer); rotationTimer = null; }
          if (currentTick)   { clearInterval(currentTick);   currentTick = null; }
        });
      }
    }

    // First render
    render(banners[0]);

    // Rotation through multiple matching banners
    if (banners.length > 1) {
      rotationTimer = setInterval(function () {
        if (paused) return;
        slot.classList.add('is-leaving');
        setTimeout(function () {
          idx = (idx + 1) % banners.length;
          render(banners[idx]);
          slot.classList.remove('is-leaving');
        }, 300); // matches CSS opacity transition
      }, rotationMs);
      // Pause on hover so users can read / click the CTA
      slot.addEventListener('mouseenter', function () { paused = true; });
      slot.addEventListener('mouseleave', function () { paused = false; });
    }
  }

  function createSlot() {
    // If no pre-rendered slot exists, create one at the top of body. Theme's
    // theme.liquid emits a <div id="kcit-banner-slot"> in the right place
    // for top-in-flow (after the header, before main content). This is a
    // fallback that runs if the slot wasn't included.
    var div = document.createElement('div');
    div.id = 'kcit-banner-slot';
    document.body.insertBefore(div, document.body.firstChild);
    return div;
  }

  /* ----- countdown helpers ----- */
  function computeCountdownContext(b, clockOffset) {
    if (!b.countdown || !b.countdown.enabled) return { html: '', suppressed: false };
    var trueNow = Date.now() + clockOffset;
    var target = pickTarget(b, trueNow);
    if (!target) return { html: '', suppressed: false };
    var remainMs = target.getTime() - trueNow;
    if (remainMs <= 0) {
      // Countdown expired — apply on_expiry
      var expiry = b.countdown.on_expiry || 'hide';
      if (expiry === 'hide') return { suppressed: true };
      if (expiry === 'stay') return { html: '' };
      if (expiry === 'message')
        return { html: '', replacementMessage: b.countdown.replacement_message || '' };
    }
    return { html: formatCountdown(remainMs, b.countdown.style || 'smart-inline'), suppressed: false };
  }
  function pickTarget(b, trueNow) {
    var target = b.countdown.target || 'end';
    if (target === 'end') return b.ends_at ? new Date(b.ends_at) : null;
    if (target === 'start') return b.starts_at ? new Date(b.starts_at) : null;
    // auto: if before start, count to start; else count to end
    if (b.starts_at) {
      var s = new Date(b.starts_at).getTime();
      if (trueNow < s) return new Date(b.starts_at);
    }
    return b.ends_at ? new Date(b.ends_at) : null;
  }
  function formatCountdown(remainMs, style) {
    var s = Math.max(0, Math.floor(remainMs / 1000));
    var d = Math.floor(s / 86400),
        h = Math.floor((s % 86400) / 3600),
        m = Math.floor((s % 3600) / 60),
        sec = s % 60;
    if (style === 'big-digits') {
      var dd = pad(d), hh = pad(h), mm = pad(m);
      return '<span class="kcit-countdown kcit-countdown--big">' +
             dd + ' : ' + hh + ' : ' + mm + '</span>';
    }
    // smart-inline
    var text;
    if (d > 0)        text = d + 'd ' + h + 'h';
    else if (h > 0)   text = h + 'h ' + m + 'm';
    else if (m > 0)   text = m + 'm ' + sec + 's';
    else              text = '0:' + pad(sec);
    return '<span class="kcit-countdown">' + text + '</span>';
  }
  function pad(n) { return n < 10 ? '0' + n : '' + n; }

  /* ----- modal (used by both `modal` and `article` cta_action) ----- */
  function openContentModal(fetchUrl, title, fallbackUrl) {
    var modal = document.createElement('div');
    modal.className = 'kcit-banner-modal';
    modal.innerHTML =
      '<div class="kcit-banner-modal__backdrop"></div>' +
      '<div class="kcit-banner-modal__box" role="dialog" aria-modal="true" aria-labelledby="kcit-modal-title">' +
        '<button class="kcit-banner-modal__close" type="button" aria-label="Close">&times;</button>' +
        '<h2 id="kcit-modal-title" class="kcit-banner-modal__title">' + escape(title || '') + '</h2>' +
        '<div class="kcit-banner-modal__body">Loading…</div>' +
      '</div>';
    document.body.appendChild(modal);
    var prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    function close() {
      modal.remove();
      document.body.style.overflow = prevOverflow;
      document.removeEventListener('keydown', onKey);
    }
    function onKey(e) { if (e.key === 'Escape') close(); }
    modal.querySelector('.kcit-banner-modal__close').addEventListener('click', close);
    modal.querySelector('.kcit-banner-modal__backdrop').addEventListener('click', close);
    document.addEventListener('keydown', onKey);

    fetch(fetchUrl, { credentials: 'same-origin' })
      .then(function (res) { if (!res.ok) throw new Error('HTTP ' + res.status); return res.text(); })
      .then(function (html) {
        modal.querySelector('.kcit-banner-modal__body').innerHTML = html;
      })
      .catch(function () {
        modal.querySelector('.kcit-banner-modal__body').innerHTML =
          '<p style="color:#8c1d1d;">Couldn\'t load page content. ' +
          '<a href="' + escape(fallbackUrl) + '">Open it directly</a>.</p>';
      });
  }

  /* ----- helpers ----- */
  function escape(s) {
    return String(s || '').replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }
})();
